import React, { useContext } from 'react';
import SocialIcons from '@repo/ui-components/dept/molecules/SocialIcons';
import Newsletter from '@repo/ui-components/dept/molecules/Newsletter';
import { DeptGrid } from '@repo/ui-components/dept/design-system';
import {
  Container,
  Box,
  Text,
  useTheme,
  GridItem,
  Image,
  Accordion,
  AccordionIcon,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Flex,
  VStack,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import Link from '@repo/ui-components/tailwind/Link';
import { NonCommerceInfoContext } from '@repo/contexts';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

const CurrentYear = () => new Date().getFullYear();

function Footer({
  footerCustomColumns,
  footerCopyright,
  footerLegal,
  socialMedia,
  newsletter,
}) {
  const theme = useTheme();
  const { isNonCommerce } = useContext(NonCommerceInfoContext);
  const { tt } = useUiComponentsInternationalContext();

  return (
    <footer>
      <Box pos="relative" zIndex={3} background="white">
        <Container
          maxW="100%"
          borderTop="1px solid"
          borderColor="stroke.light"
          id="footer-top"
        >
          <Container px={[0, null, '60px']}>
            <Box py={[10, null, 20]}>
              <DeptGrid gridRowGap="0px">
                {!isNonCommerce && (
                  <GridItem
                    colSpan={6}
                    display={{ base: 'block', lg: 'none' }}
                    mb={10}
                  >
                    <Accordion allowMultiple colSpan={6}>
                      {footerCustomColumns.map(column => (
                        <AccordionItem
                          key={column._key}
                          borderColor="global.neutral100"
                          borderTopWidth={0}
                          borderBottomWidth={1}
                        >
                          <AccordionButton
                            py={4}
                            px={0}
                            _hover={{ backgroundColor: 'initial' }}
                          >
                            <Box as="span" flex="1" textAlign="left">
                              <Text textStyle="body" fontWeight="bold">
                                {column.footerColumnHeader}
                              </Text>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>

                          <AccordionPanel pb={4} px={0} pt={0}>
                            {column.footerColumnItems.map(item => (
                              <VStack
                                key={item._key}
                                as={Link}
                                className="hover:underline focus:underline"
                                link={item}
                                alignItems="flex-start"
                              >
                                <Text pb={2} textStyle="caption">
                                  {item.footerLinkName}
                                </Text>
                              </VStack>
                            ))}
                          </AccordionPanel>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </GridItem>
                )}
                <GridItem colSpan={[6, null, 6]}>
                  <Text fontWeight="bold" pb={4}>
                    {tt(s => s.footerLayoutComponent.connectLabel)}
                  </Text>
                  <SocialIcons footerSocialIcons={socialMedia} />
                  <Newsletter newsletter={newsletter} />
                </GridItem>
                <GridItem colSpan={2} />
                {!isNonCommerce && (
                  <>
                    {footerCustomColumns.map(column => (
                      <GridItem
                        key={column._key}
                        colSpan={2}
                        display={{ base: 'none', lg: 'block' }}
                      >
                        <Text fontWeight="bold" pb={4}>
                          {column.footerColumnHeader}
                        </Text>
                        {column.footerColumnItems.map(item => (
                          <VStack
                            key={item._key}
                            as={Link}
                            className="hover:underline focus:underline"
                            link={item}
                            alignItems="flex-start"
                          >
                            <Text pb={2} textStyle="caption">
                              {item.footerLinkName}
                            </Text>
                          </VStack>
                        ))}
                      </GridItem>
                    ))}
                  </>
                )}
              </DeptGrid>
            </Box>
          </Container>
        </Container>
        <Image
          width="100%"
          src={theme.images.footer}
          display={{ base: 'none', lg: 'block' }}
          aria-hidden="true"
        />
        <Image
          width="100%"
          src={theme.images.footerMobile}
          display={{ base: 'block', lg: 'none' }}
          aria-hidden="true"
        />
        <Box background="primary.base">
          <Container
            id="footer-bottom"
            textStyle="caption"
            py={6}
            color="typography.lighter"
            display={{ base: 'block', lg: 'flex' }}
            alignItems="center"
            gap="3px"
            lineHeight="taller"
            minH="11rem"
          >
            ©
            <CurrentYear />{' '}
            <Link
              className="text-secondary-foreground hover:underline"
              link={{
                href: footerCopyright.copyrightUrl,
                linkType: 'external',
                blank: true,
              }}
            >
              {footerCopyright.copyrightName}
            </Link>
            <Text
              textStyle="caption"
              color="typography.lighter"
              lineHeight="taller"
            >
              {tt(s => s.footerLayoutComponent.rightsLabel)}
            </Text>
            <Flex flexWrap="wrap" gap="3px" pb={{ base: 16, sm: 0 }}>
              {' '}
              {/* Padding creating space for Mavenoid */}
              {footerLegal.map(item => (
                <Text
                  key={item._key}
                  textStyle="caption"
                  color="typography.lighter"
                  lineHeight="taller"
                >
                  •{' '}
                  <Link
                    className="text-secondary-foreground no-underline"
                    link={item}
                  >
                    {item.footerLinkName}
                  </Link>
                </Text>
              ))}
              <Text
                textStyle="caption"
                color="typography.lighter"
                lineHeight="taller"
                sx={{ '> button': { fontWeight: 'inherit' } }}
              >
                •{' '}
                <button
                  type="button"
                  aria-label={tt(
                    s => s.footerLayoutComponent.cookieSettingsModalAriaLabel
                  )}
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window?.OneTrust?.ToggleInfoDisplay();
                    }
                  }}
                >
                  {tt(s => s.footerLayoutComponent.cookieButton)}
                </button>
              </Text>
            </Flex>
          </Container>
        </Box>
      </Box>
    </footer>
  );
}

Footer.propTypes = {
  footerCustomColumns: PropTypes.arrayOf(
    PropTypes.shape({
      footerColumnHeader: PropTypes.string,
      footerColumnItems: PropTypes.arrayOf(
        PropTypes.shape({
          footerLinkName: PropTypes.string,
          linkType: PropTypes.string,
          href: PropTypes.string,
          internalLink: PropTypes.shape({}),
        })
      ),
    })
  ).isRequired,
  footerLegal: PropTypes.arrayOf(
    PropTypes.shape({
      linkType: PropTypes.string,
      href: PropTypes.string,
      internalLink: PropTypes.shape({}),
    })
  ).isRequired,
  footerCopyright: PropTypes.shape({
    copyrightUrl: PropTypes.string,
    copyrightName: PropTypes.string,
  }).isRequired,
  socialMedia: PropTypes.shape({
    facebookBool: PropTypes.bool,
    facebookUrl: PropTypes.string,
    instagramBool: PropTypes.bool,
    instagramUrl: PropTypes.string,
    youtubeBool: PropTypes.bool,
    youtubeUrl: PropTypes.string,
    twitterBool: PropTypes.bool,
    twitterUrl: PropTypes.string,
    pinterestBool: PropTypes.bool,
    pinterestUrl: PropTypes.string,
    tiktokBool: PropTypes.bool,
    tiktokUrl: PropTypes.string,
  }).isRequired,
  newsletter: PropTypes.shape({
    newsletterTitle: PropTypes.string,
    newsletterDescription: PropTypes.string,
  }).isRequired,
};

export default Footer;
